import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { TooltipModule } from 'primeng/tooltip';

/** Contagens do resumo da lista de usuários (cada uma vem de `GET users` com o filtro do card). */
export interface UserSummaryCounts {
  active: number;
  inactive: number;
  noPassword: number;
  passwordLinkExpired: number;
}

/** Card do resumo; clicar aplica o filtro equivalente na tabela. */
export type UserSummaryCardKey = 'active' | 'inactive' | 'noPassword' | 'passwordLinkExpired';

interface UserSummaryCard {
  key: UserSummaryCardKey;
  title: string;
  help: string;
  footer: string;
  icon: string;
  accent: string;
}

/**
 * Cards de resumo da lista de usuários (card 3697), no padrão da Gestão de Empréstimos: título, número grande e
 * ação no rodapé. Clicar num card emite a chave para a página aplicar o filtro correspondente.
 */
@Component({
  selector: 'app-user-summary-cards',
  templateUrl: './user-summary-cards.component.html',
  styleUrls: ['./user-summary-cards.component.scss'],
  standalone: true,
  imports: [CommonModule, TooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSummaryCardsComponent {
  /** Contagens; `null` enquanto carrega. */
  @Input() counts: UserSummaryCounts | null = null;
  /** Card cujo filtro está aplicado agora (destaque). */
  @Input() selected: UserSummaryCardKey | null = null;
  @Output() selectCard = new EventEmitter<UserSummaryCardKey>();

  readonly cards: UserSummaryCard[] = [
    {
      key: 'active',
      title: 'Ativos',
      help: 'Usuários que podem entrar na plataforma.',
      footer: 'Ver ativos',
      icon: 'pi-user',
      accent: '#16a34a',
    },
    {
      key: 'inactive',
      title: 'Inativos',
      help: 'Usuários inativados: continuam no histórico, mas não entram mais.',
      footer: 'Ver inativos',
      icon: 'pi-ban',
      accent: '#94a3b8',
    },
    {
      key: 'noPassword',
      title: 'Sem senha',
      help: 'Ativos que ainda não criaram a senha.',
      footer: 'Ver quem falta',
      icon: 'pi-clock',
      accent: '#f59e0b',
    },
    {
      key: 'passwordLinkExpired',
      title: 'Link de senha vencido',
      help: 'Ativos sem senha cujo último link venceu: envie um novo pelo menu da linha.',
      footer: 'Ver links vencidos',
      icon: 'pi-exclamation-circle',
      accent: '#dc3545',
    },
  ];

  /** Número do card, ou `null` enquanto carrega. */
  countOf(key: UserSummaryCardKey): number | null {
    return this.counts ? this.counts[key] : null;
  }
}
